import React, { useEffect, useState } from 'react';
import { v2 } from '@fieldnation/platform-components';
import classNames from 'classnames';

import styles from './styles.scss';
import { Part, PartUsage } from './types.d';
import TrackingField, { CarrierInfo } from '../ShipmentForm/components/TrackingField';
import {
  SELECT_OPTIONS,
  RETURN_INSTRUCTION_OPTIONS_RETURNABLE,
  RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE,
} from './utils';
import { getCarrierFromTrackingNumber } from '../ShipmentForm/utils';
import { checkExternalIdUniqueness } from './service';

const { TextInput, Select } = v2;

interface Props {
  newPart: Part;
  setNewPart: React.Dispatch<React.SetStateAction<Part>>;
  usageData: PartUsage;
  setUsageData: React.Dispatch<React.SetStateAction<PartUsage>>;
  disableExternalId?: boolean;
  isUniqueExternalId: boolean;
  setIsUniqueExternalId: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddPart = ({
  newPart,
  setNewPart,
  usageData,
  setUsageData,
  disableExternalId = false,
  isUniqueExternalId,
  setIsUniqueExternalId,
}: Props): JSX.Element => {
  const [trackingLink, setTrackingLink] = useState('');
  const [returnTrackingLink, setReturnTrackingLink] = useState('');
  const isReturnable = newPart?.returnable === 'Yes';
  const returnInstructionOptions = isReturnable
    ? RETURN_INSTRUCTION_OPTIONS_RETURNABLE
    : RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE;

  const onInputChange = (
    data: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setNewPart({
      ...newPart,
      [data.target.id]: data.target.value,
    });
  };

  const checkForExternalIdUniqueness = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!e?.target?.value) {
      return;
    }
    checkExternalIdUniqueness(
      e.target.value,
      window?.context?.company?.id || 0,
      'buyer',
    )
      .then(setIsUniqueExternalId);
  };

  const onChangeReturnable = (data): void => {
    const newReturnInstruction =
      data.value === 'Yes'
        ? RETURN_INSTRUCTION_OPTIONS_RETURNABLE?.[0].value
        : RETURN_INSTRUCTION_OPTIONS_NON_RETURNABLE?.[0].value;
    setNewPart({
      ...newPart,
      returnable: data.value,
      returnInstruction: newReturnInstruction,
    });
  };

  const onTrackingNumberChange = (carrierInfo: CarrierInfo) => {
    setNewPart({
      ...newPart,
      trackingNumber: carrierInfo.tracking,
      carrier: carrierInfo?.name ?? '',
      carrierOther: carrierInfo.other_name ?? '',
    });
    setTrackingLink(carrierInfo.tracking_link || '');
  };

  const onReturnTrackingNumberChange = (carrierInfo: CarrierInfo) => {
    setUsageData({
      ...usageData,
      returnTracking: carrierInfo.tracking,
      returnCarrier: carrierInfo?.name || '',
      returnCarrierOtherName: carrierInfo.other_name ?? '',
    });
    setReturnTrackingLink(carrierInfo.tracking_link || '');
  };

  const onChangeReturnInstruction = (option) => {
    const value =
      typeof option?.value === 'string'
        ? option.value
        : String(option.value ?? '');
    setNewPart({
      ...newPart,
      returnInstruction: value,
    });
  };

  useEffect(() => {
    if (newPart?.trackingNumber) {
      const { trackingLink: shipmentTrackingLink } =
        getCarrierFromTrackingNumber(newPart.trackingNumber);
      setTrackingLink(shipmentTrackingLink);
    }

    if (usageData?.returnTracking) {
      const { trackingLink: returnShipmentTrackingLink } =
        getCarrierFromTrackingNumber(usageData.returnTracking);
      setReturnTrackingLink(returnShipmentTrackingLink);
    }
  }, []);

  return (
    <>
      <div className={classNames(styles['multi-inputs'], styles['input-gap'])}>
        <div className={styles['input-flex']}>
          <TextInput
            id="externalId"
            label="ID"
            onChange={onInputChange}
            onBlur={checkForExternalIdUniqueness}
            value={newPart.externalId}
            disabled={disableExternalId}
            fieldValidation={isUniqueExternalId ? '' : 'error'}
          />
          {!isUniqueExternalId && (
            <p className={styles['error-message']}>ID must be unique</p>
          )}
        </div>
        <div className={styles['input-flex']}>
          <TextInput
            id="externalNumber"
            label="Number"
            onChange={onInputChange}
            value={newPart.externalNumber}
          />
        </div>
      </div>
      <div className={styles['input-gap']}>
        <TextInput
          id="description"
          label="Description"
          onChange={onInputChange}
          value={newPart.description}
        />
      </div>
      <div className={styles['input-gap']}>
        <TrackingField
          inputLabel="Tracking number"
          value={{
            name: newPart.carrier || '',
            tracking: newPart.trackingNumber ?? '',
            other_name: newPart.carrierOther ?? '',
            tracking_link: trackingLink || '',
          }}
          onChange={onTrackingNumberChange}
          disabled={false}
          errors={{ name: false, tracking: false }}
        />
      </div>
      <div>
        <div
          className={classNames(styles['multi-inputs'], styles['input-gap'])}
        >
          <div className={styles['input-flex']}>
            <Select
              id="returnable"
              label="Return defective part"
              onChange={onChangeReturnable}
              options={SELECT_OPTIONS}
              value={newPart.returnable}
            />
          </div>
          <div className={styles['input-flex']}>
            <Select
              id="returnInstruction"
              label="Instructions"
              onChange={onChangeReturnInstruction}
              options={returnInstructionOptions}
              value={String(newPart.returnInstruction || '')}
            />
          </div>
        </div>
        {isReturnable && (
          <div className={styles['input-gap']}>
            <TrackingField
              inputLabel="Return tracking number"
              value={{
                name: usageData.returnCarrier || '',
                tracking: usageData.returnTracking ?? '',
                other_name: usageData.returnCarrierOtherName ?? '',
                tracking_link: returnTrackingLink || '',
              }}
              onChange={onReturnTrackingNumberChange}
              disabled={false}
              errors={{ name: false, tracking: false }}
              optional
            />
          </div>
        )}
      </div>
    </>
  );
};

AddPart.defaultProps = {
  disableExternalId: false,
};

export default AddPart;
